import { get } from 'lodash';

import { API_NAME, FIELD_PATHS, INCOME_TYPE, REDUX_STORE } from '@funnels/constants';
import { Income, IncomeActionTypes, IncomesReducerState, Owner, SharedActionTypes } from '@funnels/types';

const shouldCalculateOwnership = (income: Omit<Income, 'clientId'>) =>
  income.type !== INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC &&
  income.type !== INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC;

const initialState: IncomesReducerState = {
  data: [],
  error: null,
  loading: 0,
};

export default function incomesReducer(
  state: IncomesReducerState = initialState,
  action: IncomeActionTypes | SharedActionTypes
): IncomesReducerState {
  switch (action.type) {
    case REDUX_STORE.SHARED_ACTION_TYPES.CLEAR_DATA:
      return {
        ...state,
        data: [],
      };
    case REDUX_STORE.SHARED_ACTION_TYPES.CLEAR_ERROR:
    case REDUX_STORE.INCOMES.ACTION_TYPES.CLEAR_INCOME_ERROR:
      return {
        ...state,
        error: null,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME:
      return {
        ...state,
        data: [...state.data, action.income],
        loading: state.loading + 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_SUCCESS:
      return {
        ...state,
        data: state.data.map((income) => (income.clientId === action.clientId ? { ...income, id: action.id } : income)),
        loading: state.loading - 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.DELETE_INCOME:
      return {
        ...state,
        data: state.data.filter((income) => income.clientId !== action.clientId),
        loading: state.loading + 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.DELETE_INCOME_SUCCESS:
      return {
        ...state,
        loading: state.loading - 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.FETCH_INCOMES:
      return {
        ...state,
        data: [],
        loading: state.loading + 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.FETCH_INCOMES_SUCCESS:
      return {
        ...state,
        data: action.incomes.map(
          (income) =>
            ({
              ...income,
              clientId: income.id,
              assetClientId: income.assetId,
              employmentClientId: income.employmentId,
              companyClientId: get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_ID(0)),
              ...(shouldCalculateOwnership(income) && {
                percentOwned: {
                  ...income.percentOwned,
                  owners: get({ incomes: [income] }, FIELD_PATHS.INCOME_OWNERS(0), []).map((o: Owner) => ({
                    ...o,
                    applicantClientId: o.applicantId,
                  })),
                },
              }),
              // TODO add client id for applicant
            } as Income)
        ),
        loading: state.loading - 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.SET_INCOME_ERROR:
      // TODO: FUNNEL-1606 - use same pattern in employment reducer, setting error message in same action as fail/abort
      return {
        ...state,
        error: {
          name: API_NAME.INCOMES,
          message: action.message,
        },
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME:
      return {
        ...state,
        data: [...state.data.map((income) => (income.clientId === action.income.clientId ? action.income : income))],
        loading: state.loading + 1,
      };
    // In employments - incomes case
    // employments.loading will become 0 once get responds in income saga (waitForResourcesToBeCreated)
    // then we trigger rest of incomes loading to 1+ so we need transaction block for preventing wrong hydrated status.
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPSERT_INCOMES:
      return {
        ...state,
        loading: state.loading + 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME_SUCCESS:
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPSERT_INCOMES_SUCCESS:
      return {
        ...state,
        loading: state.loading - 1,
      };
    case REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_FAIL:
    case REDUX_STORE.INCOMES.ACTION_TYPES.DELETE_INCOME_FAIL:
    case REDUX_STORE.INCOMES.ACTION_TYPES.FETCH_INCOMES_FAIL:
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME_FAIL:
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME_ABORT:
    case REDUX_STORE.INCOMES.ACTION_TYPES.UPSERT_INCOMES_FAIL:
      return {
        ...state,
        loading: state.loading - 1,
      };
    default:
      return state;
  }
}
